import type { EditorSDK, PageData, PageRef } from '@wix/platform-editor-sdk';
import _ from 'lodash';

import { shouldEnableExtendedUninstallMASubApps } from '../../../utils/experiments';
import { toMonitored } from '../../../utils/monitoring';
import { PERMISSION_STATES } from '../../constants';
import enforceSequentiality from '../../enforceSequentiality';
import * as membersLogic from '../../membersLogic';
import { removeMembersAreaPage } from '../../platform-api/removeMembersAreaPage';
import { getIsMigration } from '../../services/applicationState';
import { hasSocialPages } from '../../services/pages';
import { uninstallAppOnPageDelete } from '../../services/uninstall-apps';
import * as history from '../../wrappers/history';
import * as menus from '../../wrappers/menus';
import * as pages from '../../wrappers/pages';
import * as routers from '../../wrappers/routers';
import type { MembersAreaPrivateApi } from '../types';

export const createPrivateAPI = (editorSDK: EditorSDK): MembersAreaPrivateApi => ({
  hasSocialPages: () => {
    return enforceSequentiality('private-api.hasSocialPages', () => hasSocialPages(editorSDK));
  },
  removePage: (pageData) => {
    return enforceSequentiality('private-api.removePage', async () => {
      if (pageData.id) {
        await removeMembersAreaPage({ editorSDK, id: pageData.id });
      }

      if (await shouldEnableExtendedUninstallMASubApps()) {
        await uninstallAppOnPageDelete(editorSDK, pageData);
      }
    });
  },
  uninstall: () => {
    return enforceSequentiality('private-api.uninstall', () =>
      toMonitored('uninstall', () => {
        return membersLogic.uninstall(editorSDK);
      }),
    );
  },
  getRouterDataByPage: (pageRef: PageRef) => {
    return enforceSequentiality('private-api.getRouterDataByPage', () => routers.getPageRouterData(editorSDK, pageRef));
  },
  getAllRouters: () => {
    return enforceSequentiality('private-api.getAllRouters', () => routers.getAll(editorSDK));
  },
  updatePageDataInRouter: (pageRef: PageRef, dataFieldToUpdate: string, updatedData: string | object) => {
    return enforceSequentiality('private-api.updatePageDataInRouter', () =>
      routers.updatePageData(editorSDK, pageRef, dataFieldToUpdate, updatedData),
    );
  },
  saveInnerRoute: (newInnerRoute: string, pageRef: PageRef, pageRouterData: { innerRoute: string }) => {
    return enforceSequentiality('private-api.saveInnerRoute', async () => {
      let savedInnerRoute = await routers.changePagePattern(editorSDK, pageRef, newInnerRoute);
      if (savedInnerRoute && savedInnerRoute[0] === '/') {
        savedInnerRoute = savedInnerRoute.substr(1);
      }

      await menus.updateMenuItemInAllMenus({
        editorSDK,
        pageRouterData,
        updatedData: { link: { innerRoute: savedInnerRoute } },
      });
      history.addWithDelay(editorSDK, 'Members update uri seo');
    });
  },

  /* Pages */
  updatePageData: ({ pageRef, pageData }: { pageRef: PageRef; pageData: PageData }) => {
    return enforceSequentiality('private-api.updatePageData', () =>
      pages.updatePageData({ editorSDK, pageRef, pageData }),
    );
  },
  savePasswordPermissions: async (pageRef: PageRef, privacy: string, plainPassword: string) => {
    if (privacy === PERMISSION_STATES.PASSWORD && plainPassword) {
      await pages.updatePageData({ editorSDK, pageRef, pageData: { pageSecurity: { requireLogin: false } } });
      enforceSequentiality('private-api.savePasswordPermissions', () =>
        pages.updatePagePassword({ editorSDK, pageRef, plainPassword }),
      );
    } else {
      const shouldRequireLogin = !_.isEqual(privacy, PERMISSION_STATES.NONE);
      await pages.updatePageData({
        editorSDK,
        pageRef,
        pageData: { pageSecurity: { requireLogin: shouldRequireLogin } },
      });
      enforceSequentiality('private-api.removePagePassword', () => pages.removePagePassword({ editorSDK, pageRef }));
    }
  },
  /* PageInfo */
  renamePage: (pageName: string, pageRef: PageRef, pageRouterData: { innerRoute: string }) => {
    return enforceSequentiality('private-api.renamePage', async () => {
      const PpageRename = pages.rename({
        editorSDK,
        pageRef,
        title: pageName,
      });
      const PmenuUpdateItem = menus.updateMenuItemInAllMenus({
        editorSDK,
        pageRouterData,
        updatedData: { label: pageName },
      });
      const ProuterDataUpdate = routers.updatePageData(editorSDK, pageRef, 'title', pageName);
      const ProuterSeoTitleUpdate = routers.updatePageData(editorSDK, pageRef, 'seoData', { title: pageName });

      await Promise.all([PpageRename, PmenuUpdateItem, ProuterDataUpdate, ProuterSeoTitleUpdate]);
      history.add(editorSDK, 'Members page rename');
    });
  },
  createPrivateMemberPage: () => {
    throw new Error('[MAV1] PrivateAPI.createPrivateMemberPage is not implemented');
  },
  shouldAllowUpdatingPagePrefix: () => {
    throw new Error('[MAV1] PrivateAPI.shouldAllowUpdatingPagePrefix is not implemented');
  },
  openSeoRedirectDashboard: () => {
    throw new Error('[MAV1] PrivateAPI.openSeoRedirectDashboard is not implemented');
  },
  shouldShowPageRedirectNote: () => {
    throw new Error('[MAV1] PrivateAPI.shouldShowPageRedirectNote is not implemented');
  },
  refreshMembersBlocksPlatform: () => {
    throw new Error('[MAV1] PrivateAPI.refreshMembersBlocksPlatform is not implemented');
  },
  deleteApps: () => {
    throw new Error('[MAV1] PrivateAPI.deleteApps is not implemented');
  },
  getUnifiedManagerPanelData: () => {
    throw new Error('[MAV1] PrivateAPI.getUnifiedManagerPanelData is not implemented');
  },
  /* Only for templates migration to V2 */
  getIsMigrating: () => {
    return Promise.resolve(getIsMigration());
  },
});
